import { t } from '@lingui/core/macro';
import { assertPresent, isPresent, throwErrorUnlessProduction } from '@luminovo/commons';
import { useElementScrollRestoration } from '@luminovo/design-system';
import { useStorageState } from '@luminovo/frontend-utils';
import { Box } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { shallow, useSelector } from '@tanstack/react-store';
import React, { type JSX } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { TableVirtuoso } from 'react-virtuoso';
import { z } from 'zod/v4';

import { useDisplayFormat } from '../contexts/DisplayFormatContext';
import { CommandPalette } from './CommandPalette';
import { CommandContext } from './CommandPalette/types';
import { DefaultOuterEmptyPlaceholder } from './DefaultEmptyPlaceholder';
import { DefaultFallbackComponent } from './DefaultFallbackComponent';
import { DefaultScroller } from './DefaultScroller';
import { DefaultTable } from './DefaultTable';
import { DefaultTableRow } from './DefaultTableRow';
import { DefaultExpandColumn } from './ExpandColumn';
import { getFixedFooterContent } from './FixedFooterContent';
import { FixedHeaderContent } from './FixedHeaderContent';
import { FloatingSelectionBar } from './FloatingSelectionBar';
import { DefaultColumnSelection, SelectionAnchorProvider, useXKeySelectionHotkey } from './Selection';
import { usePersistedAtom } from './tableAtoms';
import { calculateBoxHeight, TanStackMenuBar } from './TanStackMenuBar';
import { useCoreTable } from './tanstackPrimitives';
import { ColumnDef, ColumnPinningState, ColumnVisibilityState, ExpandedState, RowData, Table } from './tanstackTypes';
import {
    ColumnRenderOptionsState,
    TanStackTableOverrides,
    TanStackTableProps,
    TanStackTableState,
    UseTanStackTablePaginationProps,
    UseTanStackTableProps,
    UseTanStackTableStateProps,
} from './type';
import { AdditionalFilter, FilterConfig, SupportedFilterFn } from './type';
import { useTableSearchState } from './useTableSearchState';
import { createFuzzyFilter, createPositionBasedSortFunction, getSaveAsDefaultConfig, resetTable } from './utils';

function getLeafColumnIds<TData extends RowData>(columns: ColumnDef<TData, any>[]): string[] {
    const ids: string[] = [];
    for (const col of columns) {
        const children = (col as any).columns as ColumnDef<TData, any>[] | undefined;
        if (children && children.length > 0) {
            ids.push(...getLeafColumnIds(children));
        } else if (col.id) {
            ids.push(col.id);
        }
    }
    return ids;
}

function buildFilterConfigForDataType<TData extends RowData>(
    filter: AdditionalFilter<TData, any>,
): FilterConfig<TData> {
    switch (filter.dataType) {
        case 'text':
            return { dataType: 'text', defaultFilterFn: SupportedFilterFn.includesString };
        case 'number':
            return { dataType: 'number', defaultFilterFn: SupportedFilterFn.inNumberRange };
        case 'monetaryValue':
            return { dataType: 'monetaryValue', defaultFilterFn: SupportedFilterFn.inMonetaryValueRange };
        case 'date':
            return { dataType: 'date', defaultFilterFn: SupportedFilterFn.inDateRange };
        case 'enum':
            return {
                dataType: 'enum',
                defaultFilterFn: SupportedFilterFn.equalsAny,
                options: filter.options,
                getOptionLabel: (value: any) => (isPresent(value) ? filter.getOptionLabel(value) : t`Unknown`),
                renderOption: filter.renderOption,
                getOptionCount: filter.getOptionCount,
                getOptionKey: filter.getOptionKey,
            };
        case 'array':
            return {
                dataType: 'array',
                defaultFilterFn: SupportedFilterFn.arrIncludesSome,
                options: filter.options,
                getOptionLabel: (value: any) => (isPresent(value) ? filter.getOptionLabel(value) : t`Unknown`),
                renderOption: filter.renderOption,
                getOptionCount: filter.getOptionCount,
                getOptionKey: filter.getOptionKey,
            };
        case 'quantity':
            return {
                dataType: 'quantity',
                defaultFilterFn: SupportedFilterFn.inQuantityRange,
            };
    }
}

function buildSortFnForDataType<TData extends RowData>(filter: AdditionalFilter<TData, any>) {
    switch (filter.dataType) {
        case 'monetaryValue':
            return 'monetaryValue' as const;
        case 'date':
            return 'date' as const;
        case 'quantity':
            return 'quantity' as const;
        case 'enum':
            return createPositionBasedSortFunction({
                options: filter.options,
                getOptionLabel: (value: any) => (isPresent(value) ? filter.getOptionLabel(value) : t`Unknown`),
            });
        case 'array':
            return undefined;
        default:
            return undefined;
    }
}

/** Builds hidden columns for each column's `additionalFilters`, and sets `additionalFilterColumnIds` on the parent. */
export function extractAdditionalFilterColumns<TData extends RowData>(
    columns: ColumnDef<TData, any>[],
): ColumnDef<TData, any>[] {
    const hiddenColumns: ColumnDef<TData, any>[] = [];
    const allColumnIds = new Set<string>();

    function collectExistingIds(cols: ColumnDef<TData, any>[]) {
        for (const col of cols) {
            if (col.id) allColumnIds.add(col.id);
            const children = (col as any).columns as ColumnDef<TData, any>[] | undefined;
            if (children) collectExistingIds(children);
        }
    }
    collectExistingIds(columns);

    function walk(cols: ColumnDef<TData, any>[]) {
        for (const column of cols) {
            const children = (column as any).columns as ColumnDef<TData, any>[] | undefined;
            if (children && children.length > 0) {
                walk(children);
                continue;
            }

            if (column.meta?.isAdditionalFilter) continue;

            const additionalFilters = column.meta?.additionalFilters;
            if (!additionalFilters || additionalFilters.length === 0) continue;

            const childIds: string[] = [];
            for (const filter of additionalFilters) {
                if (allColumnIds.has(filter.id)) {
                    throwErrorUnlessProduction(
                        `Error: Additional filter ID "${filter.id}" on column "${column.meta?.label()}" collides with an existing column ID.`,
                    );
                    continue;
                }
                allColumnIds.add(filter.id);

                const sortFn = buildSortFnForDataType(filter);

                const hiddenCol = {
                    id: filter.id,
                    accessorFn: filter.accessor as any,
                    filterFn: 'buildFilter' as const,
                    sortUndefined: 'last' as const,
                    enableHiding: false,
                    enableGlobalFilter: false,
                    size: 0,
                    ...(sortFn ? { sortFn: sortFn as any } : {}),
                    meta: {
                        label: filter.label,
                        description: filter.description,
                        dataType: filter.dataType,
                        filterConfig: buildFilterConfigForDataType(filter),
                        quickFilters: filter.quickFilters,
                        enableAdvancedFilters: filter.enableAdvancedFilters ?? true,
                        isAdditionalFilter: true as const,
                        parentColumnId: column.id,
                        initialVisibility: false as const,
                    },
                } satisfies ColumnDef<TData, any>;
                hiddenColumns.push(hiddenCol);
                childIds.push(filter.id);
            }

            if (childIds.length > 0 && column.meta) {
                // Safe to mutate: useColumnDefs processes the defs once per call and returns a new array.
                column.meta = { ...column.meta, additionalFilterColumnIds: childIds };
            }
        }
    }

    walk(columns);
    return hiddenColumns;
}

export function useColumnDefs<TData extends RowData>({
    columns,
    enableSelection,
    getSubRows,
    disableInferredColumns = false,
}: Pick<UseTanStackTableProps<TData>, 'columns' | 'enableSelection' | 'getSubRows'> & {
    disableInferredColumns?: boolean;
}): {
    columns: ColumnDef<TData, any>[];
    inferredColumnPinning: ColumnPinningState;
    inferredColumnVisibility: ColumnVisibilityState;
} {
    let processedColumns = columns as ColumnDef<TData, any>[];

    if (isPresent(getSubRows)) {
        processedColumns = [DefaultExpandColumn, ...columns] as ColumnDef<TData, any>[];
    }

    if (isPresent(enableSelection) && enableSelection.enabled) {
        processedColumns = [enableSelection.column ?? DefaultColumnSelection, ...processedColumns] as ColumnDef<
            TData,
            any
        >[];
    }

    const inferredColumnPinning: ColumnPinningState = { start: [], end: [] };
    const inferredColumnVisibility: ColumnVisibilityState = {};

    function walkColumns(cols: ColumnDef<TData, any>[]) {
        cols.forEach((column) => {
            const children = (column as any).columns as ColumnDef<TData, any>[] | undefined;
            if (children && children.length > 0) {
                walkColumns(children);
                return;
            }

            if (column.meta?.isAdditionalFilter) {
                if (isPresent(column.id) && column.meta?.initialVisibility !== undefined) {
                    inferredColumnVisibility[column.id] = column.meta.initialVisibility;
                }
                return;
            }

            if (column.meta?.initialPinning) {
                if (!isPresent(column.id)) {
                    throwErrorUnlessProduction(
                        `Error: The column with label "${column.meta?.label()}" requires an ID for initial pinning.`,
                    );
                    return;
                }
                if (disableInferredColumns) {
                    throwErrorUnlessProduction(
                        `Error: The column with label "${column.meta?.label()}" has initial pinning but inferred columns are disabled for pagination tables.`,
                    );
                }

                inferredColumnPinning[column.meta.initialPinning]?.push(column.id);
            }
            if (column.meta?.initialVisibility !== undefined) {
                if (!isPresent(column.id)) {
                    throwErrorUnlessProduction(
                        `Error: The column with label "${column.meta?.label()}" requires an ID to set initial visibility.`,
                    );
                    return;
                }
                if (disableInferredColumns) {
                    throwErrorUnlessProduction(
                        `Error: The column with label "${column.meta?.label()}" has initial visibility but inferred columns are disabled for pagination tables.`,
                    );
                }

                inferredColumnVisibility[column.id] = column.meta.initialVisibility;
            }
        });
    }

    const additionalColumns = extractAdditionalFilterColumns(processedColumns);
    if (additionalColumns.length > 0) {
        processedColumns = [...processedColumns, ...additionalColumns];
    }

    walkColumns(processedColumns);

    return { columns: processedColumns, inferredColumnPinning, inferredColumnVisibility };
}

const TAN_STACK_TABLE_STATE_KEY = 'version-6';

// The page re-renders on filters, sorting and search, its own state; each of the table's components subscribes to the
// rest of what it shows.
const selectNothing = () => null;

const isColumnFilters = (value: unknown): boolean =>
    z
        .array(
            z.object({
                id: z.string(),
                value: z.object({
                    filterFn: z.string(),
                    value: z.unknown(),
                }),
            }),
        )
        .safeParse(value).success;

const isSorting = (value: unknown): boolean =>
    z
        .array(
            z.object({
                id: z.string(),
                desc: z.boolean(),
            }),
        )
        .safeParse(value).success;

const isGlobalFilter = (value: unknown): boolean => typeof value === 'string';

const isFlags = (value: unknown): boolean => z.record(z.string(), z.boolean()).safeParse(value).success;

/**
 * @deprecated EXPERIMENTAL: This hook is not yet stable
 */
export function useTanStackTableState<TData extends RowData>({
    initialState,
    columnsKey,
    enableSelection,
    enableColumnHiding = false,
    enableColumnOrdering = false,
    enablePersistentColumnFilters = false,
    enablePersistentColumnOrder = true,
    enablePersistentColumnVisibility = true,
    enablePersistentColumnSizing = false,
    enableColumnResizing = false,
    enablePersistentRowSelection = false,
    enablePersistentGlobalFilter = false,
    enableSaveAsDefault = false,
    enablePersistentExpanded = false,
}: UseTanStackTableStateProps<TData>): { tableState: TanStackTableState<TData> } {
    const stableInitialState = React.useMemo(() => initialState || {}, [initialState]);

    const defaultInitialState = React.useMemo(
        () => ({
            columnFilters: stableInitialState.columnFilters ?? [],
            columnOrder: stableInitialState.columnOrder ?? [],
            columnPinning: stableInitialState.columnPinning ?? { start: [], end: [] },
            columnVisibility: stableInitialState.columnVisibility ?? {},
            columnSizing: stableInitialState.columnSizing ?? {},
            globalFilter: stableInitialState.globalFilter ?? '',
            rowSelection: stableInitialState.rowSelection ?? {},
            sorting: stableInitialState.sorting ?? [],
        }),
        [stableInitialState],
    );

    const { storage, key, enabled } = getSaveAsDefaultConfig(enableSaveAsDefault);

    const [persistentFilters, setPersistentFilters] = useStorageState(
        `${TAN_STACK_TABLE_STATE_KEY}-${key}-${columnsKey}-filters`,
        defaultInitialState.columnFilters,
        { storage, enabled },
    );

    const [persistentSorting, setPersistentSorting] = useStorageState(
        `${TAN_STACK_TABLE_STATE_KEY}-${key}-${columnsKey}-sorting`,
        defaultInitialState.sorting,
        { storage, enabled, validation: isSorting },
    );

    const [columnFilters, setColumnFilters] = useTableSearchState('columnFilters', persistentFilters, {
        enabled: enablePersistentColumnFilters,
        isValid: isColumnFilters,
    });

    const [sorting, setSorting] = useTableSearchState('sorting', persistentSorting, {
        enabled: enablePersistentColumnFilters,
        isValid: isSorting,
    });

    const columnOrder = usePersistedAtom(
        `${TAN_STACK_TABLE_STATE_KEY}-${columnsKey}-columnOrder`,
        defaultInitialState.columnOrder,
        {
            storage: localStorage,
            enabled: enablePersistentColumnOrder && enableColumnOrdering,
            isValid: (value) => z.array(z.string()).safeParse(value).success,
        },
    );

    const columnVisibility = usePersistedAtom(
        `${TAN_STACK_TABLE_STATE_KEY}-${columnsKey}-ColumnVisibility`,
        defaultInitialState.columnVisibility,
        {
            storage: localStorage,
            enabled: enablePersistentColumnVisibility && enableColumnHiding,
            isValid: isFlags,
        },
    );

    const columnSizing = usePersistedAtom(
        `${TAN_STACK_TABLE_STATE_KEY}-${columnsKey}-columnSizing`,
        defaultInitialState.columnSizing,
        {
            storage: localStorage,
            enabled: enablePersistentColumnSizing,
            isValid: (value) => z.record(z.string(), z.number()).safeParse(value).success,
        },
    );

    const [globalFilter, setGlobalFilter] = useTableSearchState('globalFilter', defaultInitialState.globalFilter, {
        enabled: enablePersistentGlobalFilter,
        isValid: isGlobalFilter,
    });

    const rowSelection = usePersistedAtom(
        `${TAN_STACK_TABLE_STATE_KEY}-${enablePersistentRowSelection}-${columnsKey}-RowSelection`,
        defaultInitialState.rowSelection,
        {
            storage: sessionStorage,
            enabled: Boolean(enablePersistentRowSelection && enableSelection?.enabled),
            isValid: isFlags,
            onChange: enableSelection?.onRowSelectionChange,
        },
    );

    const expanded = usePersistedAtom<ExpandedState>(
        `${TAN_STACK_TABLE_STATE_KEY}-${enablePersistentExpanded}-${columnsKey}-RowExtended`,
        {},
        {
            storage: sessionStorage,
            enabled: Boolean(enablePersistentExpanded),
            isValid: isFlags,
        },
    );

    const tableState = React.useMemo(
        () => ({
            initialState: stableInitialState,
            columnFilters,
            setColumnFilters,
            globalFilter,
            setGlobalFilter,
            sorting,
            setSorting,
            atoms: { columnVisibility, columnOrder, columnSizing, rowSelection, expanded },
            meta: {
                defaultInitialState,
                columnsKey,
                enableColumnHiding,
                enableColumnOrdering,
                enablePersistentColumnFilters,
                enablePersistentColumnOrder,
                enablePersistentColumnVisibility,
                enablePersistentColumnSizing,
                enableColumnResizing,
                enablePersistentGlobalFilter,
                enablePersistentRowSelection,
                enableSaveAsDefault,
                sessionFiltersAndSorting: enableSaveAsDefault
                    ? {
                          persistentFilters,
                          setPersistentFilters,
                          persistentSorting,
                          setPersistentSorting,
                      }
                    : null,
                enablePersistentExpanded,
            },
        }),
        [
            stableInitialState,
            columnFilters,
            setColumnFilters,
            globalFilter,
            setGlobalFilter,
            sorting,
            setSorting,
            columnVisibility,
            columnOrder,
            columnSizing,
            rowSelection,
            expanded,
            defaultInitialState,
            columnsKey,
            enableColumnHiding,
            enableColumnOrdering,
            enablePersistentColumnFilters,
            enablePersistentColumnOrder,
            enablePersistentColumnVisibility,
            enablePersistentColumnSizing,
            enableColumnResizing,
            enablePersistentGlobalFilter,
            enablePersistentRowSelection,
            enableSaveAsDefault,
            persistentFilters,
            setPersistentFilters,
            persistentSorting,
            setPersistentSorting,
            enablePersistentExpanded,
        ],
    );

    return { tableState };
}

/** A table with pagination, which is still rough. No row expansion: it takes no `getSubRows`, so no row expands. */
export function useTanStackTablePagination<TData extends RowData, TSharedContext>({
    data: initialData,
    columns: initialColumns,
    sharedContext,
    totalCount,
    onRowClick,
    getRowRoute,
    isLoading: inputIsLoading,
    isLoadingError,
    onRetry,
    fetchNextPage,
    isFetchingNextPage,
    enableSelection,
    tableState,
}: UseTanStackTablePaginationProps<TData, TSharedContext>): {
    table: Table<TData>;
} {
    const { columns } = useColumnDefs({
        columns: initialColumns,
        enableSelection,
        disableInferredColumns: true,
    });

    const data = initialData ?? [];
    const isLoading = inputIsLoading ?? !isPresent(initialData);

    const getIsLoading = React.useCallback(() => isLoading, [isLoading]);

    const {
        initialState,
        columnFilters,
        setColumnFilters,
        globalFilter,
        setGlobalFilter,
        sorting,
        setSorting,
        atoms,
        meta,
    } = tableState;

    const [columnRenderOptions, setColumnRenderOptions] = useStorageState<ColumnRenderOptionsState>(
        `${TAN_STACK_TABLE_STATE_KEY}-${meta.columnsKey}-renderOptions`,
        {},
        {
            storage: localStorage,
            enabled: true,
            validation: (value) => z.record(z.string(), z.string()).safeParse(value).success,
        },
    );

    const setColumnRenderOption = React.useCallback(
        (columnId: string, renderOption: string) => {
            setColumnRenderOptions((prev) => ({
                ...prev,
                [columnId]: renderOption,
            }));
        },
        [setColumnRenderOptions],
    );

    // Availability/lead time columns use a single app-wide display format instead of per-column state.
    const { displayFormat, setDisplayFormat } = useDisplayFormat();

    const table = useCoreTable(
        {
            data,
            columns,
            // This disables the built-in filtering
            manualFiltering: true,
            enableColumnResizing: meta.enableColumnResizing,
            columnResizeMode: 'onChange',
            defaultColumn: {
                minSize: 40,
            },
            state: { columnFilters, globalFilter, sorting },
            atoms,
            initialState: {
                globalFilter,
                ...initialState,
            },
            meta: {
                sharedContext,
                totalCount,
                onRowClick,
                getRowRoute,
                getIsLoading,
                isLoadingError,
                onRetry,
                fetchNextPage,
                isFetchingNextPage,
                enableSelection,
                enablePersistentScrollPosition: false,
                enableExcelExport: false,
                columnRenderOptions,
                setColumnRenderOption,
                setColumnRenderOptions,
                displayFormat,
                setDisplayFormat,
                ...meta,
            },
            globalFilterFn: 'auto',
            onColumnFiltersChange: setColumnFilters,
            onGlobalFilterChange: setGlobalFilter,
            onSortingChange: setSorting,
            getRowId: enableSelection?.getRowId,
        },
        selectNothing,
    );

    return { table };
}

/**
 * A hook to create and manage a TanStack table. This hook handles the configuration
 * and state management for the table, including data processing, column definitions,
 * and various table options.
 *
 * @example
 * const columnHelper = createColumnHelper<MyDataType>();
 * const columns = [
 *   columnHelper.accessor('name', {
 *     header: 'Name',
 *     cell: info => info.getValue()
 *   }),
 * ];
 *
 * const { table } = useTanStackTable({
 *   data: fetchData(),
 *   columns,
 *   onRowClick: (row) => console.log('Row clicked:', row)
 * });
 *
 * return <TanStackTable table={table} />;
 *
 * @see TanStackTable
 * @see createColumnHelper
 */
export function useTanStackTable<TData extends RowData, TSharedContext>({
    data: initialData,
    columns: initialColumns,
    initialState: inputInialState = {},
    enableRowSelection,
    getSubRows,
    sharedContext,
    onRowClick,
    getRowRoute,
    columnsKey: initialColumnsKey = '',
    filterFromLeafRows = false,
    isLoading: inputIsLoading,
    isLoadingError,
    onRetry,
    enableSelection,
    enableColumnHiding = false,
    enableColumnOrdering = false,
    enablePersistentColumnFilters = false,
    enablePersistentColumnOrder = true,
    enablePersistentColumnVisibility = true,
    enablePersistentColumnSizing = false,
    enableColumnResizing = false,
    enablePersistentRowSelection = false,
    enablePersistentGlobalFilter = false,
    enablePersistentScrollPosition = false,
    enableSaveAsDefault = false,
    enableExcelExport = false,
    enablePersistentExpanded = false,
}: UseTanStackTableProps<TData, TSharedContext>): {
    table: Table<TData>;
} {
    const { columns, inferredColumnPinning, inferredColumnVisibility } = useColumnDefs({
        columns: initialColumns,
        enableSelection,
        getSubRows,
    });

    const data = React.useMemo(() => initialData ?? [], [initialData]);
    const isLoading = React.useMemo(() => inputIsLoading ?? !isPresent(initialData), [inputIsLoading, initialData]);
    const getIsLoading = React.useCallback(() => isLoading, [isLoading]);

    //
    // `columnsKey` must stay stable so that dynamically added/loaded columns don't alter the columnsKey
    // (this will break state persistence - we would be writing to a different key from the one we are reading from on load)
    // Thats why we only use `initialColumns` instead of `columns` here
    const columnsKey = `${initialColumnsKey}-${getLeafColumnIds(initialColumns).join()}`;
    const [columnRenderOptions, setColumnRenderOptions] = useStorageState<ColumnRenderOptionsState>(
        `${TAN_STACK_TABLE_STATE_KEY}-${columnsKey}-renderOptions`,
        {},
        {
            storage: localStorage,
            enabled: true,
            validation: (value) => z.record(z.string(), z.string()).safeParse(value).success,
        },
    );

    const setColumnRenderOption = React.useCallback(
        (columnId: string, renderOption: string) => {
            setColumnRenderOptions((prev) => ({
                ...prev,
                [columnId]: renderOption,
            }));
        },
        [setColumnRenderOptions],
    );

    // Availability/lead time columns use a single app-wide display format instead of per-column state.
    const { displayFormat, setDisplayFormat } = useDisplayFormat();

    const {
        tableState: {
            initialState,
            columnFilters,
            setColumnFilters,
            globalFilter,
            setGlobalFilter,
            sorting,
            setSorting,
            atoms,
            meta,
        },
    } = useTanStackTableState({
        initialState: {
            columnPinning: inferredColumnPinning,
            columnVisibility: inferredColumnVisibility,
            ...inputInialState,
        },
        columnsKey,
        enableSelection,
        enableColumnHiding,
        enableColumnOrdering,
        enablePersistentColumnFilters,
        enablePersistentColumnOrder,
        enablePersistentColumnVisibility,
        enablePersistentColumnSizing,
        enableColumnResizing,
        enablePersistentRowSelection,
        enablePersistentGlobalFilter,
        enableSaveAsDefault,
        enablePersistentExpanded,
    });

    const globalFilterFn = React.useMemo(() => createFuzzyFilter(columns), [columns]);

    const table = useCoreTable(
        {
            data,
            columns,
            enableColumnResizing,
            columnResizeMode: 'onChange',
            defaultColumn: {
                minSize: 40,
            },
            state: { columnFilters, globalFilter, sorting },
            atoms,
            initialState: {
                globalFilter,
                ...initialState,
            },
            meta: {
                ...meta,
                sharedContext,
                onRowClick,
                getRowRoute,
                columnsKey,
                getIsLoading,
                isLoadingError,
                onRetry,
                enableSelection,
                enablePersistentScrollPosition,
                enableExcelExport,
                fetchNextPage: undefined,
                isFetchingNextPage: false,
                columnRenderOptions,
                setColumnRenderOption,
                setColumnRenderOptions,
                displayFormat,
                setDisplayFormat,
            },
            enableMultiRowSelection: enableSelection?.enableMultiSelection ?? true,
            // Pinned to TanStack's current default; nested-table consumers (e.g. BOM overview) rely
            // on parent toggles cascading to sub-rows.
            enableSubRowSelection: true,
            globalFilterFn,
            onColumnFiltersChange: setColumnFilters,
            onGlobalFilterChange: setGlobalFilter,
            onSortingChange: setSorting,
            getRowId: enableSelection?.getRowId,
            getSubRows,
            getColumnCanGlobalFilter: undefined,
            enableRowSelection,
            filterFromLeafRows,
        },
        selectNothing,
    );

    return { table };
}

/** Lets the `x` key toggle the hovered row; render it inside `SelectionAnchorProvider`. */
function XKeySelectionHandler(): null {
    useXKeySelectionHotkey();
    return null;
}

export function TanStackTable<TData extends RowData, TSharedContext>({
    table,
    size = 'medium',
    MenuBarTitle,
    ActionButton,
    EmptyPlaceholder,
    TableRow,
    enableMenuBar,
    overrides = {},
    enforceEmptyPlaceholder = false,
    enableFloatingSelectionBar = false,
    commands,
    primarySelectionActionSlot,
    renderSelectionHeader,
    renderSelectionCount,
    suppressHiddenSelectedWarning,
    telemetry,
}: TanStackTableProps<TData, TSharedContext>): JSX.Element {
    const {
        FallbackComponent = DefaultFallbackComponent,
        Scroller = DefaultScroller as TanStackTableOverrides<TData, TSharedContext>['Scroller'],
        Table = DefaultTable as TanStackTableOverrides<TData, TSharedContext>['Table'],
        OuterTableRow = DefaultTableRow as TanStackTableOverrides<TData, TSharedContext>['OuterTableRow'],
        OuterEmptyPlaceholder = DefaultOuterEmptyPlaceholder as TanStackTableOverrides<
            TData,
            TSharedContext
        >['OuterEmptyPlaceholder'],
    } = overrides;
    // Re-renders every row, through Virtuoso's `context`. The selection and the resize state re-render only the
    // components that show them, so ticking one row leaves the others alone.
    const { columnFilters } = useSelector(table.store, ({ rowSelection, columnResizing, ...rest }) => rest, {
        compare: shallow,
    });
    const { enablePersistentScrollPosition, fetchNextPage, columnsKey } = assertPresent(table.options.meta);
    // The app's `scrollToTopSelectors` matches this prefix, so a new history entry opens the table at the top.
    const scrollRestorationId = `tanstack-table-${columnsKey}`;
    const scrollEntry = useElementScrollRestoration({ id: scrollRestorationId });
    // On Back and Forward the router restores the position once the page has rendered, maybe before the rows load, so
    // the list starts from it itself, and the `key` below mounts it afresh when they arrive.
    const initialScrollTop = enablePersistentScrollPosition ? scrollEntry?.scrollY : undefined;
    const scrollerRef = (scroller: HTMLElement | Window | null) => {
        if (enablePersistentScrollPosition && scroller instanceof HTMLElement) {
            scroller.setAttribute('data-scroll-restoration-id', scrollRestorationId);
        }
    };
    const totalCount = table.getRowModel().rows.length;

    const isLoading = assertPresent(table.options.meta?.getIsLoading());
    const fixedHeaderContent = React.useCallback(() => <FixedHeaderContent table={table} />, [table]);
    const fixedFooterContent = React.useCallback(() => getFixedFooterContent({ table }), [table]);

    return (
        <ErrorBoundary
            fallback={(props) => {
                return <FallbackComponent {...props} onReset={() => resetTable(table)} />;
            }}
        >
            <SelectionAnchorProvider>
                <XKeySelectionHandler />
                <Box
                    sx={{
                        height: calculateBoxHeight(table, enableMenuBar, columnFilters.length > 0),
                        position: 'relative',
                    }}
                >
                    <TanStackMenuBar
                        table={table}
                        enableMenuBar={enableMenuBar}
                        ActionButton={ActionButton}
                        MenuBarTitle={MenuBarTitle}
                        enableFloatingSelectionBar={enableFloatingSelectionBar}
                    />
                    <TableVirtuoso
                        // Remounts once loaded so rows show; infinite queries skip it: each page would reset the table.
                        key={isLoading && !isPresent(fetchNextPage) ? `loading` : `done`}
                        context={{
                            table,
                            size,
                            isLoading,
                            MenuBarTitle,
                            ActionButton,
                            EmptyPlaceholder,
                            TableRow,
                        }}
                        totalCount={enforceEmptyPlaceholder ? 0 : totalCount}
                        overscan={200}
                        scrollerRef={scrollerRef}
                        initialScrollTop={initialScrollTop}
                        endReached={() => {
                            fetchNextPage?.();
                        }}
                        // Note: Do not inline the components here, every render would create new instances.
                        components={{
                            Scroller,
                            Table,
                            TableRow: OuterTableRow,
                            EmptyPlaceholder: OuterEmptyPlaceholder,
                        }}
                        fixedFooterContent={fixedFooterContent}
                        fixedHeaderContent={fixedHeaderContent}
                    />

                    <SelectionControls
                        table={table}
                        isLoading={isLoading}
                        enableFloatingSelectionBar={enableFloatingSelectionBar}
                        commands={commands}
                        primarySelectionActionSlot={primarySelectionActionSlot}
                        renderSelectionHeader={renderSelectionHeader}
                        renderSelectionCount={renderSelectionCount}
                        suppressHiddenSelectedWarning={suppressHiddenSelectedWarning}
                        telemetry={telemetry}
                    />
                </Box>
            </SelectionAnchorProvider>
        </ErrorBoundary>
    );
}

/** The floating selection bar, the command palette and its ⌘K hotkey, apart so a selection change skips the rows. */
function SelectionControls<TData extends RowData, TSharedContext>({
    table,
    isLoading,
    enableFloatingSelectionBar,
    commands = [],
    primarySelectionActionSlot,
    renderSelectionHeader,
    renderSelectionCount,
    suppressHiddenSelectedWarning = false,
    telemetry,
}: Pick<
    TanStackTableProps<TData, TSharedContext>,
    | 'table'
    | 'commands'
    | 'primarySelectionActionSlot'
    | 'renderSelectionHeader'
    | 'renderSelectionCount'
    | 'suppressHiddenSelectedWarning'
    | 'telemetry'
> & { isLoading: boolean; enableFloatingSelectionBar: boolean }): JSX.Element {
    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = React.useState(false);

    // Holds every selected ID, including rows that filters hide.
    const rowSelection = useSelector(table.store, (state) => state.rowSelection);

    // Selection counts leaves rather than top-level rows. For flat tables every flat row is a
    // leaf, so behavior is unchanged. For nested tables this means sub-row-only selection makes
    // the bar appear, and parent-cascaded selection counts each cascaded leaf instead of the
    // parent. Consumers that prefer top-level counting can introduce an opt-out prop later.
    const coreRowModel = table.getCoreRowModel();
    const selectedItems = React.useMemo(() => {
        return coreRowModel.flatRows
            .filter((row) => row.subRows.length === 0 && rowSelection[row.id])
            .map((row) => row.original);
    }, [coreRowModel.flatRows, rowSelection]);
    const totalSelectedCount = selectedItems.length;

    const filteredRowModel = table.getFilteredRowModel();
    const visibleSelectedCount = React.useMemo(() => {
        return filteredRowModel.flatRows.filter((row) => row.subRows.length === 0 && rowSelection[row.id]).length;
    }, [filteredRowModel.flatRows, rowSelection]);

    const hiddenSelectedCount = totalSelectedCount - visibleSelectedCount;

    const selectionCountLabel = renderSelectionCount?.();
    // The hidden-selected count is derived from leaf rows; when the displayed count is in a
    // different unit, the caller opts out of the (then mixed-unit) "hidden due to filters" warning.
    const effectiveHiddenSelectedCount = suppressHiddenSelectedWarning ? 0 : hiddenSelectedCount;

    const sharedContext = table.options.meta?.sharedContext as TSharedContext | undefined;

    // The palette adds `close`, `clearSelection` and `goBack`; `selectedItems` includes rows filters hide.
    const commandContext: Omit<
        CommandContext<TData, TSharedContext>,
        'close' | 'clearSelection' | 'goBack'
    > = React.useMemo(
        () => ({
            selectedItems,
            hiddenSelectedCount,
            sharedContext,
        }),
        [selectedItems, hiddenSelectedCount, sharedContext],
    );

    // Set to empty rather than reset, which would restore the initial selection.
    const handleClearSelection = React.useCallback(() => {
        table.setRowSelection({});
    }, [table]);

    const handleOpenActions = React.useCallback(() => {
        setIsCommandPaletteOpen(true);
    }, [setIsCommandPaletteOpen]);

    const toggleCommandPalette = React.useCallback(
        (e: KeyboardEvent) => {
            e.preventDefault();
            setIsCommandPaletteOpen((prev) => !prev);
        },
        [setIsCommandPaletteOpen],
    );

    useHotkeys('mod+k', toggleCommandPalette, {
        enabled: enableFloatingSelectionBar && totalSelectedCount > 0 && commands.length > 0 && !isLoading,
        enableOnFormTags: true,
    });

    const showFloatingBar = enableFloatingSelectionBar && totalSelectedCount > 0 && !isLoading;

    return (
        <>
            <FloatingSelectionBar
                open={showFloatingBar}
                selectedCount={totalSelectedCount}
                countLabel={selectionCountLabel}
                hiddenSelectedCount={effectiveHiddenSelectedCount}
                onClearSelection={handleClearSelection}
                onOpenActions={handleOpenActions}
                hasCommands={commands.length > 0}
                primaryActionSlot={primarySelectionActionSlot}
                showShortcutHint={commands.length > 0}
            />

            <CommandPalette<TData, TSharedContext>
                open={isCommandPaletteOpen}
                onOpenChange={setIsCommandPaletteOpen}
                commands={commands}
                context={commandContext}
                onClearSelection={handleClearSelection}
                renderSelectionHeader={renderSelectionHeader}
                telemetry={telemetry}
            />
        </>
    );
}
